import { endOfDay, format, isValid, parseISO, startOfDay } from 'date-fns'

import type { FetchCampaignsQueryParams } from '@/ducks/campaigns/api/campaigns.api'
import type {
  Campaign,
  CampaignDateRangePreset,
  CampaignFilterStatus,
  CampaignFilters,
  CampaignLimit,
  CampaignType,
  CampaignsResponse
} from '@/ducks/campaigns/campaigns.types'
import { EMPTY_CAMPAIGN_FILTERS } from '@/ducks/campaigns/campaigns.types'

export const CAMPAIGN_TYPE_FILTER_OPTIONS: CampaignType[] = [
  'cart_discount',
  'buyXGetYOffer',
  'food-offer'
]

export const CAMPAIGN_STATUS_FILTER_OPTIONS: CampaignFilterStatus[] = [
  1,
  2,
  'expired'
]

export const parseCampaignFilterStatusValue = (
  value: string
): CampaignFilterStatus => {
  if (value === 'expired') return 'expired'

  const status = Number(value)
  if (status === 1 || status === 2) return status

  throw new Error(`invalid campaign filter status: ${value}`)
}

export const formatCampaignFilterStatusParam = (
  statuses: CampaignFilterStatus[]
): string => statuses.map(status => String(status)).join(',')

export const CAMPAIGN_DATE_PRESET_OPTIONS: CampaignDateRangePreset[] = [
  'week',
  'month'
]

export const CAMPAIGN_DATE_PRESET_LABEL_KEYS: Record<
  CampaignDateRangePreset,
  string
> = {
  week: 'marketing.campaigns.filters.last-week',
  month: 'marketing.campaigns.filters.last-month'
}

export const CAMPAIGN_TYPE_LABEL_KEYS: Record<CampaignType, string> = {
  cart_discount: 'marketing.campaigns.filter-options.cart-discount',
  buyXGetYOffer: 'marketing.campaigns.filter-options.buy-x-get-y',
  'food-offer': 'marketing.campaigns.filter-options.item-offer'
}

export const CAMPAIGN_STATUS_LABEL_KEYS: Record<CampaignFilterStatus, string> =
  {
    1: 'marketing.campaigns.filter-options.active',
    2: 'marketing.campaigns.filter-options.inactive',
    expired: 'marketing.campaigns.filter-options.expired'
  }

export const formatCampaignFilterDate = (date: Date): string =>
  format(date, 'yyyy-MM-dd')

export const hasActiveCampaignFilters = (
  filters: CampaignFilters = EMPTY_CAMPAIGN_FILTERS
): boolean =>
  filters.campaignTypes.length > 0 ||
  filters.statuses.length > 0 ||
  filters.dateRange != null ||
  (filters.dateFrom != null && filters.dateTo != null)

const isValidIsoDate = (value?: string): value is string => {
  if (!value) return false

  const parsed = parseISO(value)
  return isValid(parsed)
}

export const isValidCustomCampaignDateRange = (
  dateFrom?: string,
  dateTo?: string
): boolean => {
  if (!dateFrom || !dateTo) return false
  if (!isValidIsoDate(dateFrom) || !isValidIsoDate(dateTo)) return false

  return dateTo >= dateFrom
}

export const filterCampaignsByCreatedAtRange = (
  campaigns: Campaign[],
  dateFrom: string,
  dateTo: string
): Campaign[] => {
  const fromMs = startOfDay(parseISO(dateFrom)).getTime()
  const toMs = endOfDay(parseISO(dateTo)).getTime()

  return campaigns.filter(
    campaign => campaign.createdAt >= fromMs && campaign.createdAt <= toMs
  )
}

export const applyCustomDateFilterToCampaignsResponse = (
  response: CampaignsResponse,
  dateFrom: string,
  dateTo: string
): CampaignsResponse => {
  const campaigns = filterCampaignsByCreatedAtRange(
    response.campaigns,
    dateFrom,
    dateTo
  )

  return {
    ...response,
    campaigns,
    total: campaigns.length
  }
}

type BuildFetchCampaignsParamsArgs = {
  page?: number
  limit?: CampaignLimit
  skip?: number
  filters?: CampaignFilters
}

export const buildFetchCampaignsParams = ({
  page,
  limit,
  skip,
  filters = EMPTY_CAMPAIGN_FILTERS
}: BuildFetchCampaignsParamsArgs): FetchCampaignsQueryParams => {
  const params: FetchCampaignsQueryParams = {
    page,
    limit
  }

  if (skip != null) {
    params.skip = skip
  } else if (page != null && limit != null) {
    params.skip = page * limit
  }

  if (filters.campaignTypes.length > 0) {
    params.campaignType = filters.campaignTypes.join(',')
  }

  if (filters.statuses.length > 0) {
    params.status = formatCampaignFilterStatusParam(filters.statuses)
  }

  if (filters.dateRange) {
    params.dateRange = filters.dateRange
    return params
  }

  if (isValidCustomCampaignDateRange(filters.dateFrom, filters.dateTo)) {
    params.dateFrom = filters.dateFrom
    params.dateTo = filters.dateTo
  }

  return params
}

export const toggleCampaignTypeFilter = (
  filters: CampaignFilters,
  campaignType: CampaignType
): CampaignFilters => {
  const nextTypes = filters.campaignTypes.includes(campaignType)
    ? filters.campaignTypes.filter(type => type !== campaignType)
    : [...filters.campaignTypes, campaignType]

  return {
    ...filters,
    campaignTypes: nextTypes
  }
}

export const toggleCampaignStatusFilter = (
  filters: CampaignFilters,
  status: CampaignFilterStatus
): CampaignFilters => {
  const nextStatuses = filters.statuses.includes(status)
    ? filters.statuses.filter(value => value !== status)
    : [...filters.statuses, status]

  return {
    ...filters,
    statuses: nextStatuses
  }
}

export const setCampaignDatePresetFilter = (
  filters: CampaignFilters,
  dateRange: CampaignDateRangePreset
): CampaignFilters => ({
  ...filters,
  dateRange,
  dateFrom: undefined,
  dateTo: undefined
})

export const setCampaignCustomDateFilter = (
  filters: CampaignFilters,
  dateFrom: string,
  dateTo: string
): CampaignFilters => ({
  ...filters,
  dateRange: undefined,
  dateFrom,
  dateTo
})

export const clearCampaignTypeFilters = (
  filters: CampaignFilters
): CampaignFilters => ({
  ...filters,
  campaignTypes: []
})

export const clearCampaignStatusFilters = (
  filters: CampaignFilters
): CampaignFilters => ({
  ...filters,
  statuses: []
})

export const clearCampaignDateFilters = (
  filters: CampaignFilters
): CampaignFilters => ({
  ...filters,
  dateRange: undefined,
  dateFrom: undefined,
  dateTo: undefined
})
